'use client';

import {
    Toast,
    ToastClose,
    ToastDescription,
    ToastProvider,
    ToastTitle,
    ToastViewport
} from '@/components/ui/Toasts/toast';
import {useToast} from '@/components/ui/Toasts/use-toast';
import {usePathname, useRouter, useSearchParams} from 'next/navigation';
import {useEffect} from 'react';
import en from '@/dictionaries/en.json';
import fi from '@/dictionaries/fi.json';

export function Toaster() {
    const {toast, toasts} = useToast();
    const searchParams = useSearchParams();
    const pathname = usePathname();
    const router = useRouter();

    useEffect(() => {
        const locale = pathname.startsWith('/fi') ? 'fi' : 'en';
        const status = searchParams.get('status');
        const status_description = searchParams.get('status_description');
        const error = searchParams.get('error');
        const error_description = searchParams.get('error_description');
        const hasEpassiToken = searchParams.has('token') && searchParams.get('token') !== '-1';
        const hasEpassiExpiration = searchParams.has('expiration_date');
        const isEpassiDashboardReturn =
            pathname.includes('/dashboard') &&
            hasEpassiToken &&
            hasEpassiExpiration;

        const statusTitle =
            isEpassiDashboardReturn
                ? (locale === 'fi'
                    ? fi.components.toasts.epassi_connected_success
                    : en.components.toasts.epassi_connected_success)
                : status ?? 'Alright!';

        if (error || status || isEpassiDashboardReturn) {
            toast({
                title: error
                    ? error ?? 'Hmm... Something went wrong.'
                    : statusTitle,
                description: error ? error_description : status_description,
                variant: error ? 'destructive' : undefined
            });

            // For Epassi callbacks, let EpassiCallbackHandler own query cleanup
            // after updateWalletToken succeeds to avoid races.
            if (isEpassiDashboardReturn) {
                return;
            }

            // Clear any 'error', 'status', 'status_description', and 'error_description' search params
            // so that the toast doesn't show up again on refresh, but leave any other search params
            // intact.
            const newSearchParams = new URLSearchParams(searchParams.toString());
            const paramsToRemove = [
                'error',
                'status',
                'status_description',
                'error_description',
            ];

            paramsToRemove.forEach((param) => newSearchParams.delete(param));
            const search = newSearchParams.toString();
            const redirectPath = search ? `${pathname}?${search}` : pathname;
            router.replace(redirectPath, {scroll: false});
        }
    }, [searchParams, pathname]);

    return (
        <ToastProvider>
            {toasts.map(function ({id, title, description, action, ...props}) {
                return (
                    <Toast key={id} {...props}>
                        <div className="grid gap-1">
                            {title && <ToastTitle>{title}</ToastTitle>}
                            {description && (
                                <ToastDescription>{description}</ToastDescription>
                            )}
                        </div>
                        {action}
                        <ToastClose/>
                    </Toast>
                );
            })}
            <ToastViewport/>
        </ToastProvider>
    );
}
